// Usage Page — billing dashboard with credit balance, plan info, and transaction history

import { useState } from "react";
import { CreditBalance } from "../components/billing/CreditBalance";
import { PricingCard } from "../components/billing/PricingCard";
import { TopUpModal } from "../components/billing/TopUpModal";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader";
import { LoadErrorState } from "../components/ui/LoadState";
import {
  useCreditBalance,
  useUsageHistory,
  useBillingPortal,
  useSubscribe,
} from "../hooks/useCredits";
import { timeAgo, formatCents } from "../lib/utils";

const REASON_LABELS: Record<string, string> = {
  content_run: "Content Run",
  subscription_renewal: "Subscription Renewal",
  topup: "Top-Up Purchase",
  signup_bonus: "Welcome Bonus",
};

export function Usage() {
  const [topUpOpen, setTopUpOpen] = useState(false);
  const creditQ = useCreditBalance();
  const usageQ = useUsageHistory();
  const { data: creditData, isLoading: creditsLoading } = creditQ;
  const { data: usageData, isLoading: usageLoading } = usageQ;
  const portalMutation = useBillingPortal();

  const subscribe = useSubscribe();

  const balance = creditData?.data;
  const transactions = usageData?.data?.transactions ?? [];
  // Never default an unknown subscription to "free": a paying user whose
  // balance failed to load would be offered Subscribe again (double billing).
  const subscriptionStatus = balance?.subscription_status ?? null;

  async function handleUpgrade() {
    try {
      const result = await subscribe.mutateAsync({
        success_url: `${window.location.origin}/usage?session=success`,
        cancel_url: `${window.location.origin}/usage?session=cancelled`,
      });
      const checkoutUrl = result.data?.checkout_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch {
      // Error handled by mutation state
    }
  }

  async function openPortal() {
    try {
      const result = await portalMutation.mutateAsync(
        `${window.location.origin}/usage`,
      );
      const portalUrl = result.data?.portal_url;
      if (portalUrl) {
        window.location.href = portalUrl;
      }
    } catch {
      // Error is handled by mutation state
    }
  }

  if (creditsLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (!balance) {
    return (
      <div className="mx-auto max-w-4xl space-y-6 py-4">
        <WorkspaceHeader
          eyebrow="Operator Workspace"
          title="Billing & Usage"
          description="Track credit balance, subscription state, and transaction history."
          breadcrumbs={[
            { label: "Workspace", href: "/home" },
            { label: "Billing & Usage" },
          ]}
        />
        <LoadErrorState
          title="Your billing status could not be loaded"
          description="We could not confirm your plan or credit balance, so subscribing and buying credits are paused until it loads. Nothing has been charged."
          onRetry={() => void creditQ.refetch()}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 py-4">
      <WorkspaceHeader
        eyebrow="Operator Workspace"
        title="Billing & Usage"
        description="Track credit balance, subscription state, and transaction history."
        breadcrumbs={[
          { label: "Workspace", href: "/home" },
          { label: "Billing & Usage" },
        ]}
        metadata={(
          <>
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              Credits / transactions
            </span>
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              {subscriptionStatus}
            </span>
          </>
        )}
      />

      {/* Monthly credits banner */}
      {subscriptionStatus === "free" && (
        <Card className="mb-6 bg-brand-50 border-brand-200">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-base font-semibold text-slate-900">
                Add monthly credits
              </h2>
              <p className="mt-0.5 text-sm text-slate-600">
                Get 20 credits per month, all AI agents, and multi-platform publishing for $20/month.
              </p>
            </div>
            <Button
              onClick={handleUpgrade}
              loading={subscribe.isPending}
              className="flex-shrink-0"
            >
              Subscribe
            </Button>
          </div>
          {subscribe.isError && (
            <p className="mt-2 text-sm text-red-600">
              Failed to start checkout. Please try again.
            </p>
          )}
        </Card>
      )}

      {/* Credit Balance */}
      <CreditBalance variant="expanded" className="mb-6" />

      {/* Actions Row */}
      <div className="mb-6 flex flex-wrap gap-3">
        <Button onClick={() => setTopUpOpen(true)} variant="primary">
          Buy Credits
        </Button>
        {subscriptionStatus === "active" && (
          <Button
            onClick={openPortal}
            variant="secondary"
            loading={portalMutation.isPending}
          >
            Manage Subscription
          </Button>
        )}
      </div>

      {/* Monthly credits card (show for free/cancelled users) */}
      {(subscriptionStatus === "free" || subscriptionStatus === "cancelled") && (
        <div className="mb-6">
          <PricingCard currentStatus={subscriptionStatus} />
        </div>
      )}

      {/* Transaction History */}
      <Card className="rounded-[20px] border border-slate-200 bg-white shadow-sm">
        <h2 className="mb-4 text-base font-semibold text-slate-900">
          Transaction History
        </h2>

        {usageLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-12 animate-pulse rounded bg-slate-100"
              />
            ))}
          </div>
        ) : usageQ.isError && !usageData ? (
          <LoadErrorState
            compact
            title="Transaction history could not be loaded"
            description="This does not mean you have no transactions."
            onRetry={() => void usageQ.refetch()}
          />
        ) : transactions.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400">
            No transactions yet.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {transactions.map((tx) => (
              <div
                key={tx.id}
                className="flex items-center justify-between py-3"
              >
                <div>
                  <p className="text-sm font-medium text-slate-900">
                    {REASON_LABELS[tx.reason] ?? tx.reason}
                  </p>
                  <p className="text-xs text-slate-400">
                    {timeAgo(tx.created_at)}
                  </p>
                </div>
                <Badge
                  variant={tx.amount > 0 ? "success" : "default"}
                >
                  {tx.amount > 0 ? "+" : ""}
                  {tx.amount} credits
                </Badge>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Top-Up Modal */}
      <TopUpModal open={topUpOpen} onClose={() => setTopUpOpen(false)} />
    </div>
  );
}
